import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';
import Badge from '../ui/Badge';
import SoundService from '../../utils/sounds';

const MOOD_OPTIONS = [
  { value: 'any', label: 'Any Mood', emoji: '🎮', description: 'Whatever sounds good' },
  { value: 'competitive', label: 'Competitive', emoji: '🏆', description: 'PvP or competitive modes' },
  { value: 'chill', label: 'Chill', emoji: '😌', description: 'Relaxed, low-stakes fun' },
  { value: 'action', label: 'Action', emoji: '💥', description: 'Fast-paced excitement' },
  { value: 'strategic', label: 'Strategic', emoji: '🧠', description: 'Think and plan together' },
  { value: 'adventure', label: 'Adventure', emoji: '🗺️', description: 'Explore new worlds' },
];

const GameNightPicker = ({ onClose }) => {
  const [mode, setMode] = useState('mood'); // 'mood', 'swipe', 'result', 'squad-setup', 'squad-swipe', 'squad-waiting', 'squad-result'
  const [selectedMood, setSelectedMood] = useState('any');
  const [statusFilter, setStatusFilter] = useState('');
  const [bothUsers, setBothUsers] = useState(true);
  const [games, setGames] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [likedGames, setLikedGames] = useState([]);
  const [skippedGames, setSkippedGames] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [finalPick, setFinalPick] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [loading, setLoading] = useState(false);

  // Squad mode state
  const [squadSession, setSquadSession] = useState(null);
  const [squadTimeout, setSquadTimeout] = useState(60);
  const [squadVotes, setSquadVotes] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [squadResults, setSquadResults] = useState(null);

  // Check for active squad sessions on mount
  useEffect(() => {
    const checkActiveSessions = async () => {
      try {
        const sessions = await api.get('/api/swipe-sessions?status=active');
        setActiveSessions(sessions);
      } catch (err) { /* ignore */ }
    };
    checkActiveSessions();
  }, []);

  const loadGames = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ online_only: 'true', limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      if (bothUsers) params.set('both_users', 'true');
      // Add mood-based filtering hints
      if (selectedMood === 'competitive') params.set('multiplayer_type', 'PvP');
      else if (selectedMood === 'chill') params.set('multiplayer_type', 'Co-op');
      const data = await api.get(`/api/games?${params}`);
      // Shuffle the games
      const shuffled = [...data.games].sort(() => Math.random() - 0.5);
      setGames(shuffled.slice(0, 20)); // Take up to 20 for swiping
      setCurrentIndex(0);
      setLikedGames([]);
      setSkippedGames([]);
    } catch (err) {
      console.error('Failed to load games:', err);
    }
    setLoading(false);
  };

  const startSwipeMode = () => {
    loadGames();
    setMode('swipe');
  };

  // Squad mode functions
  const startSquadSession = async () => {
    setLoading(true);
    try {
      const result = await api.post('/api/swipe-sessions', { timeout_minutes: squadTimeout });
      const session = await api.get(`/api/swipe-sessions/${result.session_id}`);
      setSquadSession(session);
      setGames(session.games);
      setCurrentIndex(0);
      setSquadVotes([]);
      setMode('squad-swipe');
    } catch (err) {
      alert(err.message || 'Failed to start squad session');
    }
    setLoading(false);
  };

  const joinSquadSession = async (sessionId) => {
    setLoading(true);
    try {
      const session = await api.get(`/api/swipe-sessions/${sessionId}`);
      setSquadSession(session);
      setGames(session.games);
      // Find first unvoted game
      const firstUnvoted = session.games.findIndex(g => !g.user_voted);
      setCurrentIndex(firstUnvoted >= 0 ? firstUnvoted : session.games.length);
      // Restore existing votes
      const existingVotes = session.games.filter(g => g.user_voted).map(g => ({ game_id: g.game_id, approved: g.user_approved }));
      setSquadVotes(existingVotes);
      setMode(session.current_user_completed ? 'squad-waiting' : 'squad-swipe');
    } catch (err) {
      alert(err.message || 'Failed to join session');
    }
    setLoading(false);
  };

  const handleSquadSwipe = (direction) => {
    if (currentIndex >= games.length) return;
    const currentGame = games[currentIndex];
    setSwipeDirection(direction);
    setTimeout(() => {
      setSquadVotes(prev => [...prev, { game_id: currentGame.game_id, approved: direction === 'right' }]);
      setCurrentIndex(prev => prev + 1);
      setSwipeDirection(null);
    }, 200);
  };

  const submitSquadVotes = async () => {
    setLoading(true);
    try {
      const result = await api.post(`/api/swipe-sessions/${squadSession.id}/votes`, { votes: squadVotes });
      if (result.session_completed) {
        setSquadResults(result.winner);
        setMode('squad-result');
      } else {
        setMode('squad-waiting');
      }
    } catch (err) {
      alert(err.message || 'Failed to submit votes');
    }
    setLoading(false);
  };

  const checkSquadResults = async () => {
    try {
      const results = await api.get(`/api/swipe-sessions/${squadSession.id}/results`);
      if (results.status === 'completed') {
        setSquadResults(results.winner);
        setMode('squad-result');
      }
      return results;
    } catch (err) { return null; }
  };

  // Auto-check for results when in waiting mode
  useEffect(() => {
    if (mode !== 'squad-waiting' || !squadSession) return;
    const interval = setInterval(checkSquadResults, 5000);
    return () => clearInterval(interval);
  }, [mode, squadSession]);

  const handleSwipe = (direction) => {
    if (currentIndex >= games.length) return;

    setSwipeDirection(direction);
    setTimeout(() => {
      const currentGame = games[currentIndex];
      if (direction === 'right') {
        setLikedGames(prev => [...prev, currentGame]);
      } else {
        setSkippedGames(prev => [...prev, currentGame]);
      }
      setCurrentIndex(prev => prev + 1);
      setSwipeDirection(null);
    }, 200);
  };

  const pickFromLiked = async () => {
    if (likedGames.length === 0) return;

    setSpinning(true);
    // Spin animation through liked games
    for (let i = 0; i < 12; i++) {
      setFinalPick(likedGames[Math.floor(Math.random() * likedGames.length)]);
      await new Promise(r => setTimeout(r, 100 + i * 30));
    }
    // Final pick
    const winner = likedGames[Math.floor(Math.random() * likedGames.length)];
    setFinalPick(winner);
    setSpinning(false);
    setMode('result');
  };

  const quickPick = async () => {
    setLoading(true);
    SoundService.dice();
    try {
      const params = new URLSearchParams({ online_only: 'true' });
      if (statusFilter) params.set('status', statusFilter);
      if (bothUsers) params.set('both_users', 'true');

      setSpinning(true);
      for (let i = 0; i < 15; i++) {
        const result = await api.get(`/api/games/random?${params}`);
        setFinalPick(result);
        await new Promise(r => setTimeout(r, 100 + i * 20));
      }
      setSpinning(false);
      setMode('result');
    } catch (err) {
      console.error('Quick pick failed:', err);
    }
    setLoading(false);
  };

  const resetPicker = () => {
    setMode('mood');
    setFinalPick(null);
    setGames([]);
    setCurrentIndex(0);
    setLikedGames([]);
    setSkippedGames([]);
  };

  const currentGame = games[currentIndex];
  const progress = games.length > 0 ? Math.round((currentIndex / games.length) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '28px', padding: '32px', width: '95%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }}
        onClick={e => e.stopPropagation()}
      >

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '24px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '28px' }}>🎲</span> Game Night
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >&times;</motion.button>
        </div>

        {/* Mood Selection Mode */}
        {mode === 'mood' && (
          <>
            {/* Active Squad Sessions Banner */}
            {activeSessions.length > 0 && (
              <div style={{ background: `linear-gradient(135deg, ${THEME.omega}20, ${THEME.supe}20)`, border: `1px solid ${THEME.accent}40`, borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>🎲</span>
                  <span style={{ color: THEME.accent, fontWeight: '600', fontSize: '14px' }}>Active Squad Session{activeSessions.length > 1 ? 's' : ''}!</span>
                </div>
                {activeSessions.map(s => (
                  <button key={s.id} onClick={() => joinSquadSession(s.id)} className="press-effect" style={{ width: '100%', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px 12px', marginTop: '6px', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ color: THEME.text, fontSize: '13px', fontWeight: '600' }}>Started by {s.creator_name}</div>
                    <div style={{ color: THEME.textMuted, fontSize: '11px' }}>{s.votes_completed}/{s.squad_member_count} voted • {s.game_count} games</div>
                  </button>
                ))}
              </div>
            )}

            <p style={{ color: THEME.textSecondary, textAlign: 'center', marginBottom: '20px', fontSize: '15px' }}>What's the vibe tonight?</p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
              {MOOD_OPTIONS.map(mood => (
                <button key={mood.value} onClick={() => setSelectedMood(mood.value)} style={{
                  background: selectedMood === mood.value ? `linear-gradient(135deg, ${THEME.omega}20, ${THEME.supe}20)` : THEME.bg,
                  border: `2px solid ${selectedMood === mood.value ? THEME.omega : THEME.border}`,
                  borderRadius: '14px', padding: '16px 12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '20px' }}>{mood.emoji}</span>
                    <span style={{ color: selectedMood === mood.value ? THEME.text : THEME.textSecondary, fontWeight: '600', fontSize: '14px' }}>{mood.label}</span>
                  </div>
                  <div style={{ color: THEME.textDim, fontSize: '11px', marginLeft: '30px' }}>{mood.description}</div>
                </button>
              ))}
            </div>

            {/* Solo Mode Options */}
            <div style={{ background: THEME.bg, borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter by Status</label>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: '100%', background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px 12px', color: THEME.text, fontSize: '13px' }}>
                  <option value="">All eligible games</option>
                  <option value="played">Played games</option>
                  <option value="wtp">Want to Play</option>
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: THEME.text, fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={bothUsers} onChange={e => setBothUsers(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: THEME.supe }} />
                Both users must match status
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <button onClick={startSwipeMode} disabled={loading} style={{ flex: 2, background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: `0 4px 20px ${THEME.successGlow}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>👆</span> Solo Swipe
              </button>
              <button onClick={quickPick} disabled={loading} style={{ flex: 1, background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
                Quick Pick
              </button>
            </div>

            {/* Squad Swipe Button */}
            <button onClick={() => setMode('squad-setup')} disabled={loading} style={{ width: '100%', background: `linear-gradient(135deg, ${THEME.omega}30, ${THEME.supe}30)`, color: THEME.text, border: `2px solid ${THEME.accent}60`, borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>👥</span> Squad Swipe
              <span style={{ fontSize: '11px', background: THEME.accent, color: '#000', padding: '2px 8px', borderRadius: '10px' }}>NEW</span>
            </button>
            <p style={{ color: THEME.textDim, fontSize: '11px', textAlign: 'center', marginTop: '8px' }}>Everyone swipes, then we pick from games you ALL liked!</p>
          </>
        )}

        {/* Swipe Mode */}
        {mode === 'swipe' && (
          <>
            {/* Progress bar */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', color: THEME.textMuted }}>
                <span>{currentIndex} / {games.length} reviewed</span>
                <span style={{ color: THEME.success }}>{likedGames.length} liked</span>
              </div>
              <div style={{ height: '4px', background: THEME.bg, borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, background: `linear-gradient(90deg, ${THEME.omega}, ${THEME.supe})`, transition: 'width 0.3s ease' }} />
              </div>
            </div>

            {loading ? (
              <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size={40} />
              </div>
            ) : currentGame ? (
              <div style={{ position: 'relative', marginBottom: '20px' }}>
                <div style={{
                  background: THEME.bg, borderRadius: '16px', overflow: 'hidden', transition: 'transform 0.2s ease, opacity 0.2s ease',
                  transform: swipeDirection === 'left' ? 'translateX(-100px) rotate(-10deg)' : swipeDirection === 'right' ? 'translateX(100px) rotate(10deg)' : 'none',
                  opacity: swipeDirection ? 0 : 1
                }}>
                  <img src={currentGame.header_image} alt={currentGame.name} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                  <div style={{ padding: '16px' }}>
                    <h3 style={{ margin: '0 0 8px', color: THEME.text, fontSize: '18px', fontWeight: '700' }}>{currentGame.name}</h3>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <Badge color={THEME.text} bg={THEME.bgCard}>{currentGame.multiplayer_type}</Badge>
                      {currentGame.metacritic_score && <Badge color={THEME.success} bg="rgba(16,185,129,0.15)">{currentGame.metacritic_score}</Badge>}
                    </div>
                    {currentGame.short_description && (
                      <p style={{ margin: 0, color: THEME.textSecondary, fontSize: '13px', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{currentGame.short_description}</p>
                    )}
                  </div>
                </div>

                {/* Swipe overlay indicators */}
                {swipeDirection === 'left' && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.3)', borderRadius: '16px' }}>
                    <span style={{ fontSize: '48px' }}>👎</span>
                  </div>
                )}
                {swipeDirection === 'right' && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(16,185,129,0.3)', borderRadius: '16px' }}>
                    <span style={{ fontSize: '48px' }}>👍</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ height: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: THEME.bg, borderRadius: '16px', marginBottom: '20px' }}>
                <span style={{ fontSize: '48px', marginBottom: '12px' }}>✓</span>
                <p style={{ color: THEME.textSecondary, margin: 0 }}>All games reviewed!</p>
              </div>
            )}

            {currentGame ? (
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => handleSwipe('left')} style={{ width: '80px', height: '80px', background: THEME.bg, border: `2px solid ${THEME.danger}`, borderRadius: '50%', fontSize: '32px', cursor: 'pointer', transition: 'transform 0.1s' }} title="Skip">👎</button>
                <button onClick={() => handleSwipe('right')} style={{ width: '80px', height: '80px', background: THEME.bg, border: `2px solid ${THEME.success}`, borderRadius: '50%', fontSize: '32px', cursor: 'pointer', transition: 'transform 0.1s' }} title="Like">👍</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={pickFromLiked} disabled={likedGames.length === 0 || spinning} style={{ flex: 1, background: likedGames.length > 0 ? `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})` : THEME.bgHover, color: likedGames.length > 0 ? '#000' : THEME.textMuted, border: 'none', borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', cursor: likedGames.length > 0 ? 'pointer' : 'not-allowed' }}>
                  {spinning ? 'Picking...' : `Pick from ${likedGames.length} liked`}
                </button>
                <button onClick={resetPicker} style={{ background: THEME.bgHover, color: THEME.textMuted, border: 'none', borderRadius: '12px', padding: '16px 20px', fontSize: '15px', cursor: 'pointer' }}>Start Over</button>
              </div>
            )}

            <p style={{ color: THEME.textDim, fontSize: '12px', textAlign: 'center', marginTop: '16px' }}>Swipe right on games you'd play tonight, left to skip</p>
          </>
        )}

        {/* Result Mode */}
        {mode === 'result' && finalPick && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', animation: spinning ? 'bounce 0.3s infinite' : 'none' }}>🎉</div>
            <p style={{ color: THEME.textMuted, marginBottom: '16px', fontSize: '14px' }}>Tonight's pick:</p>

            <div style={{ background: THEME.bg, borderRadius: '16px', overflow: 'hidden', marginBottom: '24px', animation: spinning ? 'pulse 0.2s infinite' : 'none' }}>
              <img src={finalPick.header_image} alt={finalPick.name} style={{ width: '100%', opacity: spinning ? 0.5 : 1, transition: 'opacity 0.2s' }} />
              <div style={{ padding: '20px' }}>
                <h3 style={{ margin: '0 0 10px', color: THEME.text, fontSize: '24px', fontWeight: '700' }}>{finalPick.name}</h3>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Badge color={THEME.text} bg={THEME.bgCard}>{finalPick.multiplayer_type}</Badge>
                  {finalPick.metacritic_score && <Badge color={THEME.success} bg="rgba(16,185,129,0.15)">Metacritic: {finalPick.metacritic_score}</Badge>}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <a href={finalPick.steam_url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span>🚀</span> Launch on Steam
              </a>
              <button onClick={resetPicker} style={{ background: THEME.bgHover, color: THEME.textMuted, border: 'none', borderRadius: '12px', padding: '16px 20px', fontSize: '15px', cursor: 'pointer' }}>Pick Again</button>
            </div>
          </div>
        )}

        {/* Squad Setup Mode */}
        {mode === 'squad-setup' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <span style={{ fontSize: '48px' }}>👥</span>
              <h3 style={{ color: THEME.text, margin: '12px 0 8px', fontSize: '18px', fontWeight: '700' }}>Squad Swipe</h3>
              <p style={{ color: THEME.textSecondary, fontSize: '13px', margin: 0 }}>Everyone swipes on the same games, then we pick from what you ALL liked!</p>
            </div>

            <div style={{ background: THEME.bg, borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Session Timeout</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                {[{ value: 5, label: '5 min' }, { value: 15, label: '15 min' }, { value: 60, label: '1 hour' }, { value: 360, label: '6 hours' }].map(opt => (
                  <button key={opt.value} onClick={() => setSquadTimeout(opt.value)} style={{
                    background: squadTimeout === opt.value ? `linear-gradient(135deg, ${THEME.omega}30, ${THEME.supe}30)` : THEME.bgCard,
                    border: `1px solid ${squadTimeout === opt.value ? THEME.accent : THEME.border}`,
                    borderRadius: '8px', padding: '10px 8px', color: squadTimeout === opt.value ? THEME.accent : THEME.textMuted,
                    fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                  }}>{opt.label}</button>
                ))}
              </div>
              <p style={{ color: THEME.textDim, fontSize: '11px', marginTop: '10px' }}>Session closes after timeout, results calculated from whoever voted</p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={startSquadSession} disabled={loading} style={{ flex: 1, background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Creating...' : '🚀 Start Session'}
              </button>
              <button onClick={() => setMode('mood')} style={{ background: THEME.bgHover, color: THEME.textMuted, border: 'none', borderRadius: '12px', padding: '16px 20px', fontSize: '15px', cursor: 'pointer' }}>Back</button>
            </div>
          </>
        )}

        {/* Squad Swipe Mode */}
        {mode === 'squad-swipe' && (
          <>
            {/* Progress bar */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', color: THEME.textMuted }}>
                <span>{currentIndex} / {games.length} reviewed</span>
                <span style={{ color: THEME.success }}>{squadVotes.filter(v => v.approved).length} liked</span>
              </div>
              <div style={{ height: '4px', background: THEME.bg, borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(currentIndex / games.length) * 100}%`, background: `linear-gradient(90deg, ${THEME.omega}, ${THEME.supe})`, transition: 'width 0.3s ease' }} />
              </div>
            </div>

            {loading ? (
              <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size={40} />
              </div>
            ) : currentIndex < games.length ? (
              <div style={{ position: 'relative', marginBottom: '20px' }}>
                <div style={{
                  background: THEME.bg, borderRadius: '16px', overflow: 'hidden', transition: 'transform 0.2s ease, opacity 0.2s ease',
                  transform: swipeDirection === 'left' ? 'translateX(-100px) rotate(-10deg)' : swipeDirection === 'right' ? 'translateX(100px) rotate(10deg)' : 'none',
                  opacity: swipeDirection ? 0 : 1
                }}>
                  <img src={games[currentIndex].header_image} alt={games[currentIndex].name} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                  <div style={{ padding: '16px' }}>
                    <h3 style={{ margin: '0 0 8px', color: THEME.text, fontSize: '18px', fontWeight: '700' }}>{games[currentIndex].name}</h3>
                    {games[currentIndex].priority_score > 1 && (
                      <Badge color={THEME.accent} bg={`${THEME.accent}20`}>🔥 {games[currentIndex].priority_score} squad members want this</Badge>
                    )}
                  </div>
                </div>
                {swipeDirection === 'left' && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.3)', borderRadius: '16px' }}>
                    <span style={{ fontSize: '48px' }}>👎</span>
                  </div>
                )}
                {swipeDirection === 'right' && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(16,185,129,0.3)', borderRadius: '16px' }}>
                    <span style={{ fontSize: '48px' }}>👍</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ height: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: THEME.bg, borderRadius: '16px', marginBottom: '20px' }}>
                <span style={{ fontSize: '48px', marginBottom: '12px' }}>✓</span>
                <p style={{ color: THEME.text, margin: 0, fontWeight: '600' }}>All {games.length} games reviewed!</p>
                <p style={{ color: THEME.textMuted, margin: '8px 0 0', fontSize: '13px' }}>You liked {squadVotes.filter(v => v.approved).length} games</p>
              </div>
            )}

            {currentIndex < games.length ? (
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => handleSquadSwipe('left')} style={{ width: '80px', height: '80px', background: THEME.bg, border: `2px solid ${THEME.danger}`, borderRadius: '50%', fontSize: '32px', cursor: 'pointer' }} title="Skip">👎</button>
                <button onClick={() => handleSquadSwipe('right')} style={{ width: '80px', height: '80px', background: THEME.bg, border: `2px solid ${THEME.success}`, borderRadius: '50%', fontSize: '32px', cursor: 'pointer' }} title="Like">👍</button>
              </div>
            ) : (
              <button onClick={submitSquadVotes} disabled={loading} style={{ width: '100%', background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Submitting...' : '📤 Submit My Votes'}
              </button>
            )}

            <p style={{ color: THEME.textDim, fontSize: '12px', textAlign: 'center', marginTop: '16px' }}>👥 Squad Session • Your votes will be combined with your squad's</p>
          </>
        )}

        {/* Squad Waiting Mode */}
        {mode === 'squad-waiting' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 2s infinite' }}>⏳</div>
            <h3 style={{ color: THEME.text, margin: '0 0 12px', fontSize: '18px', fontWeight: '700' }}>Waiting for Squad</h3>
            <p style={{ color: THEME.textSecondary, fontSize: '14px', marginBottom: '24px' }}>Your votes are in! Waiting for other squad members...</p>

            <div style={{ background: THEME.bg, borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
              <p style={{ color: THEME.textMuted, fontSize: '12px', margin: '0 0 8px', textTransform: 'uppercase' }}>Session expires</p>
              <p style={{ color: THEME.text, fontSize: '14px', fontWeight: '600', margin: 0 }}>
                {squadSession?.expires_at ? new Date(squadSession.expires_at).toLocaleTimeString() : 'Soon'}
              </p>
            </div>

            <p style={{ color: THEME.textDim, fontSize: '12px' }}>Results will show automatically when everyone votes or timeout occurs</p>

            <button onClick={async () => {
              const results = await checkSquadResults();
              if (results?.status !== 'completed') {
                alert('Still waiting for other squad members...');
              }
            }} style={{ marginTop: '16px', background: THEME.bgHover, color: THEME.textMuted, border: 'none', borderRadius: '12px', padding: '12px 20px', fontSize: '14px', cursor: 'pointer' }}>
              Check Results
            </button>
          </div>
        )}

        {/* Squad Result Mode */}
        {mode === 'squad-result' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <p style={{ color: THEME.textMuted, marginBottom: '16px', fontSize: '14px' }}>The squad has spoken!</p>

            {squadResults ? (
              <>
                <div style={{ background: THEME.bg, borderRadius: '16px', overflow: 'hidden', marginBottom: '16px' }}>
                  <img src={squadResults.header_image} alt={squadResults.name} style={{ width: '100%' }} />
                  <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 10px', color: THEME.text, fontSize: '24px', fontWeight: '700' }}>{squadResults.name}</h3>
                    <Badge color={THEME.accent} bg={`${THEME.accent}20`}>
                      {squadResults.approval_type === 'unanimous' ? '✨ Unanimous!' : `👍 ${squadResults.approval_count} votes`}
                    </Badge>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <a href={`steam://run/${squadResults.game_id}`} style={{ flex: 1, background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span>🚀</span> Let's Play!
                  </a>
                  <button onClick={resetPicker} style={{ background: THEME.bgHover, color: THEME.textMuted, border: 'none', borderRadius: '12px', padding: '16px 20px', fontSize: '15px', cursor: 'pointer' }}>New Session</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ background: THEME.bg, borderRadius: '16px', padding: '32px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '48px' }}>😔</span>
                  <h3 style={{ color: THEME.text, margin: '16px 0 8px', fontSize: '18px' }}>No Consensus</h3>
                  <p style={{ color: THEME.textSecondary, fontSize: '13px', margin: 0 }}>Nobody agreed on any games! Try again with different choices?</p>
                </div>
                <button onClick={resetPicker} style={{ width: '100%', background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '12px', padding: '16px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
                  🔄 Try Again
                </button>
              </>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Legacy alias
const RandomPicker = GameNightPicker;

export { GameNightPicker as RandomPicker };
export default GameNightPicker;
